import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import {
  IGenericFlagMgt
} from "@blcloud/bl-datamodel";
import * as Cookies from "js-cookie";
import { Const, LocalStorageUtils } from "@blcloud/bl-common";
import {
  GenericFlagMgtResource,
  UserAuthInfoResource,
  UserLoginInfoResource,
} from "@blcloud/bl-ng-resource";
import { Rules } from "@blcloud/bl-ng-ui-component";
import { OpenAMService } from "@blcloud/bl-ng-share-module";
import { catchError, finalize, map } from "rxjs/operators";
import { of as RxOf } from "rxjs/observable/of";
import {
  BlApiClient,
  BlApiConditionHelper,
  BlApiQueryParams,
  BlApiSearchCondition,
} from "@blcloud/bl-ng-common";
import { isEmpty as _isEmpty } from "lodash";

@Injectable()
export class LoginPageService {
  /**
   * ローカルストレージKey
   */
  readonly LocalStorageKey = {
    /** ログイン後初回フラグ */
    InitAfterLogin: 'initAfterLogin',
  };

  /**
   * コンストラクタ
   * @param genericFlagMgtResource 汎用フラグ管理サービス
   * @param userLoginInfoResource 利用者ログイン情報リソース
   * @param openAMService
   */
  constructor(
    private genericFlagMgtResource: GenericFlagMgtResource,
    private userLoginInfoResource: UserLoginInfoResource,
    private openAMService: OpenAMService,
    private userAuthInfoResource: UserAuthInfoResource,
    private apiClient: BlApiClient
  ) {
  }

  /**
   * ログアウト処理
   */
  logout(): Observable<any> {
    const body = { tokenId: Cookies.get(Const.Cookie.Key.ACCESS_TOKEN) };
    const options = {
      subject: 'common',
      entityOptions: { operation: 'logout/' },
      apiOptions: { ignoreAuthError: true },
    };
    return this.userLoginInfoResource.post(body, options).pipe(
      catchError(() => {
        return RxOf(null);
      }),
      finalize(() => {
        // 認証情報を削除
        this.clearLoginCookie().subscribe();
      })
    );
  }

  /**
   * getFlag
   */
  getFlag(key: string, fnc: (res: IGenericFlagMgt) => void) {
    this.genericFlagMgtResource.getById(key, { subject: 'common' }
    ).subscribe((res) => {
      fnc(res);
    }, (err) => {
      fnc((<IGenericFlagMgt>{
        blTenantId: '',
        genericFlagKey: key,
        genericFlagValue: err.status !== 404
      }));
    });
  }

  setLoginCookie(res: string[]): Observable<void> {
    // 第二レベルドメインがない想定でドメイン名を第三レベルドメインで抜き出す
    const dns = document.domain.split('.').slice(-2).join('.');
    const ok = dns.match(/bcloud.jp$/) ? true : false; // 本番のみtrueとする
    // 認証情報のセット
    Cookies.set(Const.Cookie.Key.ACCESS_TOKEN, res[0], { secure: ok });
    Cookies.set(Const.Cookie.Key.ID_TOKEN, res[1], { secure: ok });

    // OpenAMのCookieにアクセストークンを登録
    return this.openAMService.setCookies({
      [Const.Cookie.Key.ACCESS_TOKEN]: Cookies.get(Const.Cookie.Key.ACCESS_TOKEN)
    }).pipe(catchError(() => RxOf<void>(undefined)));
  }

  clearLoginCookie(): Observable<void> {
    Cookies.remove(Const.Cookie.Key.ACCESS_TOKEN); // ログイン画面で矯正削除
    Cookies.remove(Const.Cookie.Key.ID_TOKEN); // ログイン画面で矯正削除
    Cookies.remove(Const.Cookie.Key.YELLOWFIN_JWT);
    Cookies.remove(Const.Cookie.Key.BP_KEYCLOAK_REFRESH);
    // OpenAMのCookieからアクセストークンを削除
    return this.openAMService.setCookies({
      [Const.Cookie.Key.ACCESS_TOKEN]: ''
    }).pipe(catchError(() => RxOf<void>(undefined)));
  }

  rules(): Observable<Rules[]> {
    return this.userLoginInfoResource.getRules<Rules[]>({ subject: 'common', apiOptions: { anonymous: true } });
  }

  /**
   * ローカルストレージにログイン後初回フラグを書き込みます。
   * @param flag ログイン後初回フラグ
   */
  saveLocalStorageInitAfterLogin(flag: boolean): void {
    LocalStorageUtils.save(this.LocalStorageKey.InitAfterLogin, flag);
  }

  /**
   * 利用者認証情報を取得する
   * @param accountId アカウントID
   * @returns 利用者認証情報
   */
  getUserAuthInfo(accountId: string): Observable<any> {
    const params = new BlApiQueryParams();
    const conditions = [
      BlApiConditionHelper.makeCondStrEqual("accountId", accountId),
    ];

    const apiCondition = new BlApiSearchCondition();
    apiCondition.addCondition(conditions);
    params.setCondition(apiCondition);

    const searchParam = {
      params,
      subDomain: "contract",
      entityId: "userauthinfo",
      subject: "common",
    };

    // 検索APIをコールし、結果のリストから先頭1件を返す
    return this.userAuthInfoResource.get<any>(searchParam).pipe(
      map((response) => {
        if (_isEmpty(response) || _isEmpty(response.searchResultList)) {
          return null;
        } else {
          return response.searchResultList[0];
        }
      }),
    );
  }

  /**
   * パスワード強制変更用のチケットを発行する
   * @returns 発行されたチケット文字列 (token)
   */
  issuePasswordChangeTicket(email: string, passwordChangeFlg: boolean): Observable<string> {
    const apiParams = {
      subDomain: "contract",
      entityId: "passwordchangeticket",
      subject: "common",
    };

    const options = {
      body: {
        emailAddress: email,
        requiresPassWordChangeFlg: passwordChangeFlg
      },
    };

    return this.apiClient.post(apiParams, options).pipe(
      map((response: any) => {
        if (_isEmpty(response) || _isEmpty(response.receiptTicket)) {
          return null;
        } else {
          return response.receiptTicket;
        }
      }),
      catchError(() => RxOf(null)),
    );
  }

  /**
   * 利用者基本情報からメールアドレスを取得する
   * @param blUserId ユーザーID（authInfoに含まれているID）
   */
  getUserEmail(blUserId: string): Observable<string | null> {
    const params = new BlApiQueryParams();
    const conditions = [
      // 検索条件: blUserId:eq:○○
      BlApiConditionHelper.makeCondStrEqual("blUserId", blUserId),
    ];

    const apiCondition = new BlApiSearchCondition();
    apiCondition.addCondition(conditions);
    params.setCondition(apiCondition);

    const apiParams = {
      subDomain: "contract",
      entityId: "userinformation/all",
      subject: "search",
    };

    // 検索APIの呼び出し（getSearch または get でパラメータを渡す）
    return this.apiClient.get(apiParams, { params }).pipe(
      map((response: any) => {
        if (_isEmpty(response) || _isEmpty(response.searchResultList)) {
          return null;
        }

        const userInfo = response.searchResultList[0];

        // userContactInfoList)の中から、種別=3(メール)を探す
        if (userInfo.userContactInfoList && userInfo.userContactInfoList.length > 0) {
          const emailContact = userInfo.userContactInfoList.find(
            (c: any) => c.blUserContactType === "3" || c.blUserContactTypeEnumName === "メールアドレス"
          );

          // メールアドレスが見つかったらその文字列を返す
          if (emailContact && emailContact.blUserContactInfo) {
            return emailContact.blUserContactInfo;
          }
        }
        return null; // メアドが登録されていない場合
      }),
      catchError(() => RxOf(null))
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/page/login-page/login-page.service.ts